import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import React, { useState } from 'react';
import { RiLoader2Line, RiCheckboxCircleFill } from 'react-icons/ri';

import styles from './index.module.scss';

import { STREAMHUB_RTMP_URL, DJI_APP_ID, DJI_APP_KEY, DJI_APP_LICENSE } from '@/config';

const cx = classNames.bind(styles);

const Page = () => {
  const [licenseState, setLicenseState] = useState(false);
  const [platformState, setPlatformState] = useState(false);
  const [remoteControllerSN, setRemoteControllerSN] = useState();
  const [aircraftSN, setAircraftSN] = useState();
  const [loadedModules, setLoadedModules] = useState({
    cloud: false,
    liveStreaming: false,
  });

  // Verify DJI application license
  const loadLicense = () => {
    return new Promise((resolve, reject) => {
      const result = window.djiBridge.platformVerifyLicense(DJI_APP_ID, DJI_APP_KEY, DJI_APP_LICENSE);
      const { code, data } = JSON.parse(result);

      if (code === 0 && data) {
        setLicenseState(true);
        resolve();
      } else {
        reject('⛔️ window.djiBridge.platformVerifyLicense');
      }
    });
  };

  // Verify DJI platform initialization status
  const loadPlatform = () => {
    return new Promise((resolve, reject) => {
      const result = window.djiBridge.platformIsVerified();
      const { code, data } = JSON.parse(result);

      if (code === 0 && data) {
        setPlatformState(true);
        resolve();
      } else {
        reject('⛔️ window.djiBridge.platformIsVerified');
      }
    });
  };

  // Retrieve remote controller serial number
  const getRemoteControllerSN = () => {
    return new Promise((resolve, reject) => {
      const result = window.djiBridge.platformGetRemoteControllerSN();
      const { code, data } = JSON.parse(result);

      if (code === 0) {
        setRemoteControllerSN(data);
        resolve();
      } else {
        reject('⛔️ window.djiBridge.platformGetRemoteControllerSN');
      }
    });
  };

  // Retrieve aircraft serial number
  const getAircraftSN = () => {
    return new Promise((resolve, reject) => {
      const result = window.djiBridge.platformGetAircraftSN();
      const { code, data } = JSON.parse(result);

      if (code === 0) {
        setAircraftSN(data);
        resolve(data);
      } else {
        reject('⛔️ window.djiBridge.getAircraftSN');
      }
    });
  };

  /**
   * Load and initialize DJI Cloud API module
   * Establishes MQTT connection with configured credentials
   */
  const loadCloudModule = () => {
    window.djiBridge.platformLoadComponent(
      'thing',
      JSON.stringify({
        host: `tcp://${process.env.REACT_APP_MQTT_DJI_HOST}:${process.env.REACT_APP_MQTT_DJI_PORT}`,
        username: process.env.REACT_APP_MQTT_DJI_USERNAME,
        password: process.env.REACT_APP_MQTT_DJI_PASSWORD,
        connectCallback: 'connectCallback',
      })
    );
  };

  // Fetch robot id from M1UCS API
  const fetchRobotId = async (sn) => {
    if (!sn) {
      console.error('Aircraft SN is not available');
      return null;
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}/eg/robots/dji/${sn}`);
    const { success, data } = await response.json();
    if (!success) return null;

    return data.robotId;
  };

  /**
   * Load and initialize live streaming module
   * Configures RTMP endpoint and starts video transmission
   * @param {string} sn - Aircraft serial number
   */
  const loadLiveStreamingModule = async (sn) => {
    // Fetch robot id before starting live streaming
    const robotId = await fetchRobotId(sn);
    if (!robotId) return;

    window.djiBridge.platformLoadComponent(
      'liveshare',
      JSON.stringify({ videoPublishType: 'video-demand-aux-manual' })
    );

    window.djiBridge.liveshareSetConfig(2, JSON.stringify({ url: `${STREAMHUB_RTMP_URL}/live/${robotId}` })); // 2: RTMP
    window.djiBridge.liveshareStartLive();

    // Update UI state after starting live streaming
    setLoadedModules((prev) => ({ ...prev, liveStreaming: true }));
  };

  useMountEffect(() => {
    // Store aircraft SN in a variable accessible to callbacks
    let currentAircraftSN = null;

    // Callback invoked when Cloud API module successfully connects
    const connectCallback = () => {
      setLoadedModules((prev) => ({ ...prev, cloud: true }));
      loadLiveStreamingModule(currentAircraftSN);
    };

    // Handler for platform stop event
    // Cleans up loaded modules to prevent resource leaks
    const onStopPlatform = () => {
      window.djiBridge.platformUnloadComponent('thing');
      window.djiBridge.platformUnloadComponent('liveshare');
    };

    // Handler for back button click event
    const onBackClick = () => {
      // TODO
    };

    // Register callbacks on global window object for DJI Bridge access
    window.connectCallback = connectCallback;
    window.djiBridge.onStopPlatform = onStopPlatform;
    window.djiBridge.onBackClick = onBackClick;

    (async () => {
      try {
        await loadLicense();
        await loadPlatform();
        await getRemoteControllerSN();
        currentAircraftSN = await getAircraftSN();

        // Configure platform metadata
        window.djiBridge.platformSetInformation('M1UCS', 'M1UCS', '모빌리티원');
        window.djiBridge.platformSetWorkspaceId('e3dea0f5-37f2-4d79-ae58-490af3228069');

        // Only load modules if initialization succeeded
        loadCloudModule();
      } catch (error) {
        console.error('DJI platform initialization failed:', error);
      }
    })();

    return () => {
      // Remove global callback references
      delete window.connectCallback;
      delete window.djiBridge.onStopPlatform;
      delete window.djiBridge.onBackClick;

      // Unload modules if they were loaded
      try {
        window.djiBridge.platformUnloadComponent('thing');
        window.djiBridge.platformUnloadComponent('liveshare');
      } catch (error) {
        console.error('Error unloading DJI modules:', error);
      }
    };
  });

  return (
    <div className={cx('container')}>
      <div className={cx('group')}>
        <div className={cx('title')}>Initialize...</div>
        <ul>
          <li>
            <div className={cx('label')}>Verify License</div>
            {!licenseState && <RiLoader2Line size={16} className={cx('rotating')} />}
            {licenseState && <RiCheckboxCircleFill size={16} color="green" />}
          </li>
          <li>
            <div className={cx('label')}>Verify Platform</div>
            {!platformState && <RiLoader2Line size={16} className={cx('rotating')} />}
            {platformState && <RiCheckboxCircleFill size={16} color="green" />}
          </li>
          <li>
            <div className={cx('label')}>Get Remote Controller SN</div>
            {!remoteControllerSN && <RiLoader2Line size={16} className={cx('rotating')} />}
            {remoteControllerSN && (
              <div className={cx('value')}>
                <RiCheckboxCircleFill size={16} color="green" />
                <div className={cx('sn')}>{remoteControllerSN}</div>
              </div>
            )}
          </li>
          <li>
            <div className={cx('label')}>Get Aircraft SN</div>
            {!aircraftSN && <RiLoader2Line size={16} className={cx('rotating')} />}
            {aircraftSN && (
              <div className={cx('value')}>
                <RiCheckboxCircleFill size={16} color="green" />
                <div className={cx('sn')}>{aircraftSN}</div>
              </div>
            )}
          </li>
        </ul>
      </div>
      <div className={cx('group')}>
        <div className={cx('title')}>Load Components...</div>
        <ul>
          <li>
            <div className={cx('label')}>Thing</div>
            {!loadedModules.cloud && <RiLoader2Line size={16} className={cx('rotating')} />}
            {loadedModules.cloud && <RiCheckboxCircleFill size={16} color="green" />}
          </li>
          <li>
            <div className={cx('label')}>Liveshare</div>
            {!loadedModules.liveStreaming && <RiLoader2Line size={16} className={cx('rotating')} />}
            {loadedModules.liveStreaming && <RiCheckboxCircleFill size={16} color="green" />}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Page;
